// Plugin de optimización para formularios inteligentes
import { App } from 'vue'
import { 
  createFormLazyLoaders, 
  useComponentPreloader, 
  useDeviceOptimization 
} from '@/composables/useLazyLoading'

export interface FormOptimizationOptions {
  enablePreloading?: boolean
  enableLazyLoading?: boolean
  enableDeviceOptimization?: boolean
  preloadOnIdle?: boolean
  preloadDelay?: number
  enablePerformanceMonitoring?: boolean
  logLevel?: 'none' | 'error' | 'warn' | 'info' | 'debug'
}

const defaultOptions: FormOptimizationOptions = {
  enablePreloading: true,
  enableLazyLoading: true,
  enableDeviceOptimization: true,
  preloadOnIdle: true,
  preloadDelay: 2000,
  enablePerformanceMonitoring: true,
  logLevel: 'info'
}

class FormOptimizationManager {
  private options: FormOptimizationOptions
  private preloader: ReturnType<typeof useComponentPreloader>
  private deviceOptimization: ReturnType<typeof useDeviceOptimization>
  private formLoaders: ReturnType<typeof createFormLazyLoaders>
  private performanceObserver: PerformanceObserver | null = null
  private preloadScheduled = false

  constructor(options: FormOptimizationOptions) {
    this.options = { ...defaultOptions, ...options }
    this.preloader = useComponentPreloader()
    this.deviceOptimization = useDeviceOptimization()
    this.formLoaders = createFormLazyLoaders()
    
    this.initializeOptimizations()
  }

  private log(level: string, message: string, ...args: any[]) {
    const levels = ['none', 'error', 'warn', 'info', 'debug']
    const currentLevel = levels.indexOf(this.options.logLevel || 'info')
    const messageLevel = levels.indexOf(level)
    
    if (messageLevel <= currentLevel && currentLevel > 0) {
      console[level as keyof Console](`[FormOptimization] ${message}`, ...args)
    }
  }

  private initializeOptimizations() {
    this.log('info', 'Inicializando optimizaciones de formularios...')
    
    if (this.options.enableDeviceOptimization) {
      this.setupDeviceOptimization()
    }
    
    if (this.options.enablePerformanceMonitoring) {
      this.setupPerformanceMonitoring()
    }
    
    if (this.options.enablePreloading) {
      this.schedulePreloading()
    }
  }

  private setupDeviceOptimization() {
    const { isLowEnd, connection, shouldPreload } = this.deviceOptimization
    
    this.log('info', 'Configuración del dispositivo:', {
      isLowEnd: isLowEnd.value,
      connection: connection.value,
      shouldPreload: shouldPreload.value
    })

    // Ajustar configuraciones basadas en el dispositivo
    if (isLowEnd.value) {
      this.options.enablePreloading = false
      this.options.preloadDelay = 5000
      this.log('warn', 'Dispositivo de gama baja detectado, ajustando optimizaciones')
    }

    if (connection.value?.saveData) {
      this.options.enablePreloading = false
      this.log('warn', 'Modo de ahorro de datos detectado, desactivando precarga')
    }
  }

  private setupPerformanceMonitoring() {
    if (!('PerformanceObserver' in window)) {
      this.log('warn', 'PerformanceObserver no disponible')
      return
    }

    try {
      this.performanceObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            this.log('debug', 'Métricas de navegación:', {
              loadTime: entry.loadEventEnd - entry.loadEventStart,
              domContentLoaded: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
              totalTime: entry.loadEventEnd - entry.fetchStart
            })
          }
          
          if (entry.entryType === 'measure' && entry.name.includes('form-component')) {
            this.log('debug', `Tiempo de carga del componente ${entry.name}:`, entry.duration)
            
            // Si un componente tarda mucho en cargar, ajustar configuraciones
            if (entry.duration > 1000) {
              this.log('warn', `Componente ${entry.name} tardó ${entry.duration}ms en cargar`)
            }
          }
        }
      })

      this.performanceObserver.observe({ 
        entryTypes: ['navigation', 'measure', 'paint'] 
      })
    } catch (error) {
      this.log('error', 'Error configurando PerformanceObserver:', error)
    }
  }

  private schedulePreloading() {
    if (this.preloadScheduled || !this.options.enablePreloading) {
      return
    }

    this.preloadScheduled = true

    const schedulePreload = () => {
      if (this.options.preloadOnIdle && 'requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.executePreloading()
        }, { timeout: this.options.preloadDelay })
      } else {
        setTimeout(() => {
          this.executePreloading()
        }, this.options.preloadDelay)
      }
    }

    // Esperar a que la página esté completamente cargada
    if (document.readyState === 'complete') {
      schedulePreload()
    } else {
      window.addEventListener('load', schedulePreload, { once: true })
    }
  }

  private async executePreloading() {
    this.log('info', 'Iniciando precarga de componentes de formularios...')
    
    const startTime = performance.now()
    
    try {
      const success = await this.preloader.preloadFormComponents()
      const duration = performance.now() - startTime
      
      if (success) {
        this.log('info', `✅ Precarga completada exitosamente en ${duration.toFixed(2)}ms`)
      } else {
        this.log('warn', `⚠️ Precarga completada con errores en ${duration.toFixed(2)}ms`)
      }
      
      // Marcar la métrica de rendimiento
      performance.mark('form-components-preloaded')
      performance.measure('form-preload-time', 'navigationStart', 'form-components-preloaded')
      
    } catch (error) {
      this.log('error', 'Error durante la precarga:', error)
    }
  }

  // Métodos públicos para la aplicación
  getFormComponent(name: 'incident' | 'user' | 'inventory', options = {}) {
    const optimizedOptions = {
      ...this.deviceOptimization.getOptimizationConfig(),
      ...options
    }

    switch (name) {
      case 'incident':
        return this.formLoaders.useSmartIncidentForm(optimizedOptions)
      case 'user':
        return this.formLoaders.useUserFormDialog(optimizedOptions)
      case 'inventory':
        return this.formLoaders.useInventoryRequestForm(optimizedOptions)
      default:
        throw new Error(`Componente de formulario desconocido: ${name}`)
    }
  }

  async preloadFormComponent(name: string) {
    this.log('info', `Precargando componente ${name}...`)
    const startTime = performance.now()
    
    try {
      let loader: () => Promise<any>
      
      switch (name) {
        case 'incident':
          loader = () => import('@/components/forms/SmartIncidentForm.vue')
          break
        case 'user':
          loader = () => import('@/components/forms/UserFormDialog.vue')
          break
        case 'inventory':
          loader = () => import('@/components/forms/InventoryRequestForm.vue')
          break
        default:
          throw new Error(`Componente desconocido: ${name}`)
      }
      
      const success = await this.preloader.preloadComponent(name, loader)
      const duration = performance.now() - startTime
      
      if (success) {
        this.log('info', `✅ ${name} precargado en ${duration.toFixed(2)}ms`)
      }
      
      return success
    } catch (error) {
      this.log('error', `Error precargando ${name}:`, error)
      return false
    }
  }

  getPreloadStatus() {
    return {
      preloaded: this.preloader.preloadedComponents.value,
      preloading: this.preloader.preloadingComponents.value,
      deviceInfo: {
        isLowEnd: this.deviceOptimization.isLowEnd.value,
        connection: this.deviceOptimization.connection.value,
        shouldPreload: this.deviceOptimization.shouldPreload.value
      }
    }
  }

  destroy() {
    if (this.performanceObserver) {
      this.performanceObserver.disconnect()
    }
  }
}

// Plugin de Vue
export const FormOptimizationPlugin = {
  install(app: App, options: FormOptimizationOptions = {}) {
    const manager = new FormOptimizationManager(options)
    
    // Proveer el manager globalmente
    app.provide('formOptimization', manager)
    
    // Agregar propiedades globales
    app.config.globalProperties.$formOptimization = manager
    
    // Cleanup en unmount de la app
    const originalUnmount = app.unmount
    app.unmount = function() {
      manager.destroy()
      originalUnmount.call(this)
    }
  }
}

// Composable para usar el manager
export const useFormOptimization = () => {
  const manager = inject('formOptimization') as FormOptimizationManager
  
  if (!manager) {
    throw new Error('FormOptimizationPlugin no está instalado')
  }
  
  return manager
}

// Directiva personalizada para lazy loading automático
export const vLazyForm = {
  mounted(el: HTMLElement, binding: any) {
    const { value: componentName, modifiers } = binding
    
    if (!('IntersectionObserver' in window)) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Precargar el componente cuando entra en viewport
            const manager = el.__formOptimizationManager
            if (manager) {
              manager.preloadFormComponent(componentName)
            }
            
            observer.unobserve(el)
          }
        })
      },
      {
        rootMargin: modifiers.eager ? '0px' : '100px',
        threshold: 0.1
      }
    )

    observer.observe(el)
    
    // Guardar referencia para cleanup
    el.__intersectionObserver = observer
  },
  
  unmounted(el: HTMLElement) {
    if (el.__intersectionObserver) {
      el.__intersectionObserver.disconnect()
    }
  }
}

export default FormOptimizationPlugin
